import React, { useState, useEffect, useRef } from "react";
import TeaserAggregator from "./TeaserAggregator";
import TeaserAggregatorForm from "./TeaserAggregatorForm";
import withQs from "../../utils/WithQs";
import useThis from "../../hooks/useThis";
import {
  ScraperItemsListService,
  ScraperItemsRefreshService
} from "../../utils/api";

import { teaserStatus, campaignTypes } from "../../constants.js";

import useEventEmitter from "../../hooks/useEventEmitter";
import useSearch from "../../hooks/useSearch";
import { MyCardsService } from "../../utils/api";

import useCampaignInfoEffect from "../../effects/useCampaignInfoEffect";

import { hasExchangeCampaignAccess } from "../../utils/perm";
import usePermissions from "../../hooks/usePermissions";

const perPage = 20;

const TeaserAggregatorView = ({ location, history }) => {
  const [search, setSearch] = useSearch({}, location, history);
  const {
    info: campaignInfo,
    loading: campaignInfoLoading
  } = useCampaignInfoEffect(search.campaign);
  const thisSearch = useThis(search);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [pageInfo, setPageInfo] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [processItems, setProcessItems] = useState([]);
  const [processIndex, setProcessIndex] = useState(null);
  const [scroll, setScroll] = useState(0);

  const [refreshLoading, setRefreshLoading] = useState(false);

  const [userCardsLoading, setUserCardsLoading] = useState(false);

  const eventEmitter = useEventEmitter();

  const thisCampaignInfo = useThis(campaignInfo);

  const permissions = usePermissions();

  const hasMoreRef = useRef(hasMore);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const itemsLoadingRef = useRef(itemsLoading);
  useEffect(() => {
    itemsLoadingRef.current = itemsLoading;
  }, [itemsLoading]);

  const onCampaignSelect = item => {
    setSearch({ campaign: item.value });
  };

  const onPeriodChange = period => {
    setSearch({ period });
  };

  const onCheckChange = ({ id, checked }) => {
    if (checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(itemId => itemId != id));
    }
  };

  const getCampaign = () => {
    if (search.campaign) {
      return parseInt(search.campaign);
    }
    return null;
  };

  const getPeriod = () => {
    return parseInt(search.period) || 1;
  };

  const onNextLoad = () => {
    if (itemsLoadingRef.current) {
      return;
    }

    if (hasMoreRef.current) {
      setItemsLoading(true);

      setPageInfo(prev => {
        return { limit: perPage, offset: prev.offset + perPage };
      });
    }
  };

  const onNextClick = () => {
    setProcessItems(items.filter(item => selectedItems.includes(item.id)));
    setProcessIndex(0);
  };

  const getProcessingItem = () => {
    if (processItems.length == 0) {
      return {};
    }

    const item = {
      id: processItems[processIndex].id,
      cid: search.campaign,
      title: processItems[processIndex].title,
      desc: processItems[processIndex].description,
      url: processItems[processIndex].link,
      image_url: processItems[processIndex].is_image_valid
        ? processItems[processIndex].image
        : null,
      from_scraper: true,
      published: teaserStatus.active,
      type: 2 // inf by default
    };

    return item;
  };

  const onDone = () => {
    if (processIndex == processItems.length - 1) {
      setProcessIndex(0);
      setProcessItems([]);
      setSelectedItems([]);
      return;
    }
    setProcessIndex(prev => prev + 1);
  };

  const onRefreshClick = () => {
    setRefreshLoading(true);

    ScraperItemsRefreshService({ campaign: search.campaign })
      .then(data => {
        if (data.new_items_cnt != 0) {
          setItems([]);
          setSelectedItems([]);
          setProcessItems([]);
          setProcessIndex(0);
          setPageInfo({ limit: perPage, offset: 0 });
        }
      })
      .catch(e => {
        console.log(e);
      })
      .finally(() => {
        setRefreshLoading(false);
      });
  };

  const loadUserCards = () => {
    setUserCardsLoading(true);

    MyCardsService()
      .then(cards => {
        setUserCardsLoading(false);
        if (cards.length != 0) {
          if (thisSearch.current.campaign != cards[0].id) {
            setSearch({ campaign: cards[0].id });
          }
        } else {
          const isAdvert = thisCampaignInfo.current.type == campaignTypes.ad;
          const allowAnyExchange = hasExchangeCampaignAccess(permissions);

          if (!(isAdvert || allowAnyExchange)) {
            setSearch({ campaign: "" });
          }
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  useEffect(() => {
    loadUserCards();

    eventEmitter.subscribe("cards_update", loadUserCards);

    return () => {
      eventEmitter.unsubscribe("cards_update", loadUserCards);
    };
  }, []);

  useEffect(() => {
    if (!search.campaign) {
      setItems([]);
      return;
    }

    setItems([]);
    setHasMore(true);
    setPageInfo({ limit: perPage, offset: 0 });
  }, [search]);

  useEffect(() => {
    if (!search.campaign) {
      return;
    }

    setItemsLoading(true);

    ScraperItemsListService({
      campaign: getCampaign(),
      period: getPeriod(),
      limit: pageInfo.limit,
      offset: pageInfo.offset
    })
      .then(data => {
        if (data.items.length == 0) {
          setHasMore(false);
        }
        setItems([...items, ...data.items]);
      })
      .catch(e => {
        console.log(e);
      })
      .finally(() => {
        setItemsLoading(false);
      });
  }, [pageInfo]);

  if (processItems.length == 0) {
    return (
      <TeaserAggregator
        campaign={getCampaign()}
        onCampaignSelect={onCampaignSelect}
        period={getPeriod()}
        onPeriodChange={onPeriodChange}
        items={items}
        selectedItems={selectedItems}
        onCheckChange={onCheckChange}
        hasMore={hasMore && (itemsLoading || userCardsLoading)}
        onNextLoad={onNextLoad}
        onNextClick={onNextClick}
        refreshLoading={refreshLoading}
        onRefreshClick={onRefreshClick}
        scroll={scroll}
        setScroll={setScroll}
      />
    );
  }

  return (
    <TeaserAggregatorForm
      item={getProcessingItem()}
      onDone={onDone}
      key={getProcessingItem().id}
      currentItemNum={processIndex}
      totalItemsNum={processItems.length}
    />
  );
};

export default TeaserAggregatorView;
